import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Dropdown } from "antd";
import type { DropDownProps } from "antd/lib/dropdown";

import { cn } from "@btnet/utilities-core";

import { useMenuFocusOnOpen } from "../BuiMenu/useMenuFocusOnOpen";
import "./BuiDropdown.less";

/**
 * BuiDropdown props - extends antd's full `DropDownProps` with `testid` and
 * `triggerRef`. The full antd surface is intentionally preserved so consumers
 * (and the core `BTDropdown` wrapper) have the same contract as the antd
 * primitive — see the public-API note in the ticket.
 */
export interface IBuiDropdownProps extends DropDownProps {
  children?: ReactNode;
  /** Required test identifier for testing. */
  testid: string;
  /**
   * Ref to the trigger element. When provided, focus returns here when the menu
   * closes. Wire the same ref to the trigger element (e.g. a button's `innerRef`)
   * for focus return.
   */
  triggerRef?: RefObject<HTMLElement | null>;
  /**
   * When `false`, the dropdown does not move focus to the menu on open, return
   * focus to the trigger on close, or handle Escape-to-close. Set this for
   * combobox-style dropdowns whose trigger is a text input the user types into
   * (e.g. BTTimePicker): focusing the menu would steal focus and drop keystrokes.
   * @default true
   */
  manageFocus?: boolean;
  /**
   * When `false`, focus is not returned to the trigger when the menu closes, while
   * focus-into-menu on open and Escape-to-close still work. Set this when selecting a menu
   * item hands focus to its own target (e.g. an inline input) that the trigger focus-return
   * would otherwise steal focus from.
   * @default true
   */
  returnFocusOnClose?: boolean;
}

/** rc-dropdown fires `onOverlayClick` with a MenuInfo object whose real DOM target lives
 * at `domEvent.target`. `domEvent` is optional because non-Menu overlays (or certain
 * rc-dropdown code paths) can fire `onOverlayClick` with a plain DOM event that has no
 * `domEvent` property. */
interface IRCOverlayClickEvent {
  domEvent?: { target: HTMLElement | null };
}

const defaultTriggers: IBuiDropdownProps["trigger"] = ["click"];
const defaultAlign: IBuiDropdownProps["align"] = { offset: [0, 0] };

/**
 * BuiDropdown component - a decorative, managed dropdown wrapper around antd Dropdown.
 * Handles visibility state and keyboard accessibility internally with Blueprint styling.
 *
 * This is the decorative `Bui*` layer: no analytics, hotkeys, business logic, or app
 * context. Cross-layer concerns (e.g. analytics) belong in the `BTDropdown` core wrapper.
 *
 * Built-in behavior:
 * - Click trigger to open/close
 * - Escape key closes dropdown
 * - Focus transfers to menu on open (for keyboard navigation)
 * - Focus returns to trigger on close (when `triggerRef` is provided)
 * - Menu closes on item click
 *
 * For keyboard focus return, pass a `triggerRef` and wire the same ref to the trigger element.
 * `getPopupContainer` is a pure prop — supply an app-specific resolver (e.g. closest modal)
 * from the consuming layer rather than coupling this component to the app.
 * @param props - The component props.
 * @param props.testid - Test ID for testing.
 * @param props.overlay - Menu content (typically a BuiMenu component).
 * @param props.triggerRef - Ref to trigger element for focus return on close.
 * @param props.trigger - Trigger mode(s). Defaults to ["click"].
 * @param props.disabled - Whether the dropdown is disabled.
 * @example
 * ```tsx
 * const triggerRef = useRef<HTMLElement>(null);
 *
 * <BuiDropdown
 *   testid="actions-dropdown"
 *   triggerRef={triggerRef}
 *   overlay={
 *     <BuiMenu testid="actions-menu">
 *       <BuiMenu.Item testid="edit">Edit</BuiMenu.Item>
 *       <BuiMenu.Item testid="delete">Delete</BuiMenu.Item>
 *     </BuiMenu>
 *   }
 * >
 *   <BuiButton innerRef={triggerRef} testid="trigger" icon={<IconDotsThree />} />
 * </BuiDropdown>
 * ```
 */
export const BuiDropdown = (props: IBuiDropdownProps) => {
  const {
    trigger = defaultTriggers,
    align = defaultAlign,
    overlayStyle,
    overlayClassName,
    overlay,
    children,
    className,
    testid,
    triggerRef,
    onVisibleChange,
    manageFocus = true,
    returnFocusOnClose = true,
    ...otherProps
  } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const fallbackRef = useRef<HTMLElement>(null);

  // Extra RAF delay gives rc-align time to complete first-open popup positioning
  // before useMenuFocusOnOpen's double-RAF fires menu.focus(). On first open the
  // popup starts at top:0; without this delay, focus() scrolls to the page top.
  const [focusVisible, setFocusVisible] = useState(false);
  useEffect(() => {
    if (!visible) {
      setFocusVisible(false);
      return;
    }
    const id = requestAnimationFrame(() => setFocusVisible(true));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  const setVisibleAndNotify = useCallback(
    (newVisible: boolean) => {
      onVisibleChange?.(newVisible);
      setVisible(newVisible);
    },
    [onVisibleChange],
  );

  // Close on scroll anywhere in the page (ado-283026). Scroll events don't bubble, so a
  // bubble-phase document listener only ever sees the window/document scrolling — it misses
  // scrolling an inner overflow:auto container (a data grid, a sticky-header page body, etc.),
  // which is the common case for a dropdown whose trigger lives outside any scrollable ancestor
  // of its own. The capture phase, by contrast, sees every scroll in the document regardless of
  // where it originates, so this needs no knowledge of the trigger's specific scroll container.
  // Scrolling the overlay's own menu content (which can exceed its max-height) is excluded —
  // that's the user interacting with the open menu, not a signal to dismiss it.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const handleScroll = (e: Event) => {
      if (
        e.target instanceof Element &&
        e.target.closest(".BuiDropdownOverlay")
      ) {
        return;
      }
      setVisibleAndNotify(false);
    };
    document.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });
    return () =>
      document.removeEventListener("scroll", handleScroll, { capture: true });
  }, [visible, setVisibleAndNotify]);

  const { handleVisibleChange } = useMenuFocusOnOpen({
    triggerRef: triggerRef ?? fallbackRef,
    visible: focusVisible,
    onVisibleChange: setVisibleAndNotify,
    enabled: manageFocus,
    returnFocusOnClose,
  });

  // Workaround for rc-dropdown 3.2.5 bug: in controlled mode (`visible` prop),
  // clicking a menu item calls internal `setTriggerVisible(false)` but never calls
  // `onVisibleChange(false)`, so the dropdown stays open. `onOverlayClick` IS called
  // on item click, so we use it to close the dropdown.
  //
  // Skip closing when the click hits a nested BuiDropdown trigger — otherwise opening
  // a nested dropdown (e.g. a BuiDropdownButton rolled up into an overflow menu) would
  // close this dropdown and leave the nested one orphaned in its own portal.
  //
  // rc-dropdown wires `onOverlayClick` to the overlay's onClick, which antd Menu fires
  // with a MenuInfo object (`{ key, keyPath, item, domEvent }`), not a MouseEvent — so
  // the real DOM target lives at `e.domEvent.target`.
  const handleOverlayClick = useCallback(
    (e: IRCOverlayClickEvent) => {
      const target = e.domEvent?.target;
      if (target?.closest(".BuiDropdown") || target?.closest(".BTDropdown")) {
        return;
      }
      setVisibleAndNotify(false);
    },
    [setVisibleAndNotify],
  );

  return (
    <Dropdown
      overlay={overlay}
      align={align}
      visible={visible}
      overlayClassName={cn(overlayClassName, "BuiDropdownOverlay")}
      trigger={trigger}
      // rc-dropdown applies opacity 0 to the overlay when it's hidden, but this causes issues with testing-library's `toBeVisible` assertion. Setting opacity to 1 here and relying on display: none for visibility control instead.
      overlayStyle={{ opacity: 1, ...overlayStyle }}
      className={cn(className, "BuiDropdown")}
      onVisibleChange={handleVisibleChange}
      data-testid={testid}
      // We have to use otherProps here pending https://github.com/react-component/dropdown/pull/202
      {...otherProps}
      // rc-dropdown supports onOverlayClick but antd's DropDownProps doesn't expose it.
      // This is needed to close the dropdown on item click in controlled mode (visible prop).
      {...({ onOverlayClick: handleOverlayClick } as Record<string, unknown>)}
    >
      {children}
    </Dropdown>
  );
};
